/**
 * Per-client Meta feeds + OAuth.
 *
 * Mirrors `ClientGoogleFeedsPage.js` so the two per-client integration hubs
 * feel like a matched set. The page is purely about:
 *   • connecting (or reusing the master) Meta Business OAuth, and
 *   • assigning each discovered resource (Ad Account / Page / Instagram /
 *     WhatsApp Business Account) to a property within this client.
 *
 * Reporting, ad insights and organic data fetching live elsewhere — this
 * page only owns the wiring step.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Facebook, Instagram, MessageSquare, Megaphone, CheckCircle2, Loader2,
  RefreshCw, Trash2, Plus, AlertCircle, AlertTriangle, ChevronDown,
  ChevronRight, ExternalLink, ShieldCheck, ShieldAlert, ShieldQuestion,
  Database,
} from "lucide-react";

const META_SERVICES = [
  { icon: Megaphone, color: "#1877F2", label: "Meta Ads" },
  { icon: Facebook, color: "#1877F2", label: "Facebook Pages" },
  { icon: Instagram, color: "#E1306C", label: "Instagram Business" },
  { icon: MessageSquare, color: "#25D366", label: "WhatsApp Business" },
];

export default function ClientMetaAdsPage() {
  const { clientId } = useParams();
  const [busy, setBusy] = useState(false);
  const [refreshingAssets, setRefreshingAssets] = useState(false);
  const [connection, setConnection] = useState(null);
  const [resources, setResources] = useState({ loading: true });
  const [businessUnits, setBusinessUnits] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loadError, setLoadError] = useState(null);

  // ---------- Loaders ----------
  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const [conn, res, bu, asg] = await Promise.all([
        api.get(`/clients/${clientId}/meta/connection`).then((r) => r.data),
        api.get(`/clients/${clientId}/meta/resources`).then((r) => r.data)
          .catch(() => ({ resources: {} })),
        api.get(`/clients/${clientId}/business-units`).then((r) => r.data)
          .catch(() => []),
        api.get(`/clients/${clientId}/meta/resource-assignments`)
          .then((r) => r.data).catch(() => ({ assignments: {} })),
      ]);
      setConnection(conn || { connected: false });
      setResources({ ...(res?.resources || {}), loading: false });
      setBusinessUnits(bu || []);
      setAssignments(asg?.assignments || {});
    } catch (e) {
      logger.warn("Failed to load Meta data", e?.response?.data);
      setLoadError(e?.response?.data?.detail || "Failed to load Meta data");
      setResources({ loading: false });
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Detect ?connected=1 bounce from the unified OAuth callback page.
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("connected") === "1"
        && u.searchParams.get("provider") === "meta") {
      toast.success("Meta account connected — assets ready to assign.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // ---------- Actions ----------
  const startOAuth = async () => {
    setBusy(true);
    try {
      // Reuse the master OAuth callback URL — Meta only allows whitelisted
      // redirect URIs, so consolidating to one path keeps the App-dashboard
      // config minimal. The callback routes per-client vs master via the
      // state prefix (`mts_` => per-client).
      const redirectUri = `${window.location.origin}/integrations/meta/callback`;
      sessionStorage.setItem("meta_oauth_client_id", clientId);
      const { data } = await api.post(
        `/clients/${clientId}/meta/oauth/start`,
        { redirect_uri: redirectUri },
      );
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Meta OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this client's Meta connection?")) return;
    try {
      await api.delete(`/clients/${clientId}/meta/disconnect`);
      toast.success("Disconnected");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  const refreshAssets = async () => {
    setRefreshingAssets(true);
    try {
      // Trigger a hierarchy re-sync if there's a per-client OAuth; for the
      // master-only flow the refresh is implicit (live enrich) — refetching
      // /resources is enough.
      if (connection?.connection_source !== "master") {
        try {
          await api.post(`/clients/${clientId}/meta/sync/hierarchy`);
        } catch (e) {
          // Sync only works when the per-client OAuth exists; ignore the
          // 404/403 and just refetch the resources endpoint.
          logger.warn("Hierarchy sync skipped", e?.response?.data);
        }
      }
      await refresh();
      toast.success("Assets refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingAssets(false);
    }
  };

  const assignResource = useCallback(async (service, resource_id,
                                            resource_label,
                                            business_unit_id, status) => {
    const key = `${service}::${resource_id}`;
    // Optimistic update — snap the dropdown immediately.
    setAssignments((prev) => {
      const next = { ...prev };
      if (status === "unassigned") {
        delete next[key];
      } else {
        next[key] = {
          service, resource_id, resource_label,
          business_unit_id: status === "assigned" ? business_unit_id : null,
          status,
        };
      }
      return next;
    });
    try {
      await api.post(`/clients/${clientId}/meta/resource-assignments`, {
        service, resource_id, resource_label,
        business_unit_id: status === "assigned" ? business_unit_id : null,
        status,
      });
      toast.success(
        status === "assigned" ? "Assigned to property"
        : status === "ignored" ? "Ignored — excluded from reports"
        : "Cleared",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      // Rollback via authoritative refetch.
      try {
        const { data } = await api.get(
          `/clients/${clientId}/meta/resource-assignments`);
        setAssignments(data?.assignments || {});
      } catch (_) { /* ignore */ }
    }
  }, [clientId]);

  const connected = !!connection?.connected;
  const masterAvailable = !!connection?.master_available;
  const isMaster = connection?.connection_source === "master";

  return (
    <div className="space-y-6" data-testid="client-meta-feeds-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Connections · Meta</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Meta feeds
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Connect this client&apos;s Meta Business account (or reuse the master
            agency grant) to pull Facebook Pages, Instagram Business and
            Ads Manager assets. Each asset can then be assigned to a property
            inside this client so it lands in the right report.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh}
          className="rounded-sm" data-testid="meta-feeds-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loadError && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {loadError}
        </div>
      )}

      {/* App-review banner */}
      <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
        data-testid="meta-review-banner">
        <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1 text-[12px] leading-relaxed">
          <span className="font-semibold">Meta Business Verification + App Review required.</span>{" "}
          Meta gates advertising scopes
          (<span className="font-mono mx-0.5">ads_read</span>,
          <span className="font-mono mx-0.5">ads_management</span>,
          <span className="font-mono mx-0.5">read_insights</span>) behind
          App Review. Until approval, OAuth completes but the Graph API
          rejects data calls.{" "}
          <a href="https://developers.facebook.com/docs/development/release/business-verification"
            className="underline inline-flex items-center gap-0.5"
            target="_blank" rel="noreferrer">
            docs <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      <ConnectionGroup
        title="Primary Meta Business account"
        subtitle="One Business Login grants Facebook Pages, Instagram Business, Ads Manager and WhatsApp Business in a single consent flow."
        icon={<Facebook className="h-5 w-5 text-[#1877F2]" />}
        color="#1877F2"
        services={META_SERVICES}
        connection={connection}
        connected={connected}
        isMaster={isMaster}
        masterAvailable={masterAvailable}
        busy={busy}
        refreshingAssets={refreshingAssets}
        onConnect={startOAuth}
        onDisconnect={disconnect}
        onRefreshAssets={refreshAssets}
        resources={resources}
        businessUnits={businessUnits}
        assignments={assignments}
        onAssign={assignResource}
        ctaLabel={connected ? "Reconnect" : "Connect Meta account"}
        emptyHint={
          masterAvailable
            ? "A master Meta OAuth exists but no assets have been assigned to this client yet. Open the master integrations page to assign Ad Accounts / Pages, or click Connect above to launch a per-client Business Login flow."
            : "No Meta account connected yet — clicking Connect will redirect to Meta's Business Login consent screen. Pick this client's business portfolio and the assets to share."
        }
      />
    </div>
  );
}

function ConnectionGroup({ title, subtitle, icon, color, services, connection,
                          connected, isMaster, masterAvailable, busy,
                          refreshingAssets, onConnect, onDisconnect,
                          onRefreshAssets, resources, businessUnits,
                          assignments, onAssign, ctaLabel, emptyHint }) {
  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden">
      <div className="h-1.5" style={{ background: color }} />
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center"
              style={{ background: `${color}14` }}>
              {icon}
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">{title}</h2>
              <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {connected && (
              <Button size="sm" variant="outline"
                className="rounded-sm whitespace-nowrap"
                onClick={onRefreshAssets} disabled={refreshingAssets}
                data-testid="meta-refresh-assets"
                title="Re-fetch the live asset list from Meta">
                {refreshingAssets
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Refresh assets
              </Button>
            )}
            <Button onClick={onConnect} disabled={busy}
              className="rounded-sm text-white whitespace-nowrap"
              style={{ background: color }}
              data-testid="meta-connect-primary">
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <Plus className="h-4 w-4 mr-2" />}
              {ctaLabel}
            </Button>
          </div>
        </div>

        {/* Service chips */}
        <div className="flex flex-wrap gap-2">
          {services.map((s) => {
            const SIcon = s.icon;
            return (
              <span key={s.label}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-zinc-200 bg-zinc-50 text-[11px] text-zinc-700">
                <SIcon className="h-3.5 w-3.5" style={{ color: s.color }} />
                {s.label}
              </span>
            );
          })}
        </div>

        {/* Connected status + connection metadata */}
        {!connection ? (
          <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading connection…
          </div>
        ) : !connected ? (
          <>
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-4 text-[12px] text-zinc-600">
              {emptyHint}
            </div>
            {masterAvailable && (
              <Link to="/integrations/meta"
                className="inline-flex items-center gap-1 text-[12px] text-[#1877F2] hover:underline"
                data-testid="meta-master-link">
                Open master Meta integrations
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </>
        ) : (
          <>
            <div className="rounded-sm border border-zinc-200 overflow-hidden">
              <div className="flex items-center justify-between gap-3 p-3 hover:bg-zinc-50/50 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {isMaster
                    ? <Database className="h-4 w-4 text-zinc-500 shrink-0" />
                    : <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate"
                      data-testid="meta-row-name">
                      {connection.meta_user_name || (isMaster ? "Master OAuth · agency grant" : "(unknown user)")}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">
                      {connection.meta_user_email || ""}
                      {connection.login_mode && (
                        <> · {connection.login_mode === "business"
                              ? "Login for Business"
                              : "Classic OAuth"}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <HealthPill connection={connection} />
                  {!isMaster && (
                    <Button size="sm" variant="ghost"
                      className="text-red-600 rounded-sm"
                      onClick={onDisconnect}
                      data-testid="meta-disconnect">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {connection.last_error && (
                <div className="px-3 py-2 text-[11px] text-red-800 bg-red-50 border-t border-red-100 flex items-start gap-1.5">
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span className="font-mono break-all">{connection.last_error}</span>
                </div>
              )}

              {/* Resource panel — Ad Accounts → Pages → Instagram → WABA */}
              <ResourcesPanel resources={resources}
                businessUnits={businessUnits}
                assignments={assignments}
                onAssign={onAssign} />
            </div>

            {isMaster && (
              <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 flex items-start gap-3 flex-wrap"
                data-testid="meta-override-cta">
                <div className="flex-1 min-w-0">
                  <div className="label-overline text-blue-900 mb-1">
                    Need scoped tokens for this client?
                  </div>
                  <p className="text-[12px] text-blue-900 leading-relaxed">
                    This client is currently sharing the master agency OAuth.
                    Click Connect above to launch Meta&apos;s Login for Business
                    flow and give this client its own non-expiring System User
                    token with the full Marketing API scope set.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ResourcesPanel({ resources, businessUnits, assignments, onAssign }) {
  if (resources.loading) {
    return (
      <div className="border-t border-zinc-100 bg-zinc-50/40 px-3 py-2.5 text-[11px] text-zinc-500 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Discovering assets…
      </div>
    );
  }

  const sections = [
    {
      key: "ad_accounts",
      label: "Meta Ads accounts",
      icon: <Megaphone className="h-3.5 w-3.5" style={{ color: "#1877F2" }} />,
      items: resources.ad_accounts || [],
      renderItem: (a) => (
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <span className="font-mono text-[12px] truncate">
            {a.name || a.meta_ad_account_id}
          </span>
          <span className="text-[10px] font-mono text-zinc-500">
            {a.meta_ad_account_id}
          </span>
          {a.currency && (
            <span className="text-[10px] font-mono text-zinc-500">· {a.currency}</span>
          )}
        </div>
      ),
      resourceId: (a) => String(a.meta_ad_account_id),
      resourceLabel: (a) => a.name || a.meta_ad_account_id,
    },
    {
      key: "pages",
      label: "Facebook Pages",
      icon: <Facebook className="h-3.5 w-3.5" style={{ color: "#1877F2" }} />,
      items: resources.pages || [],
      renderItem: (p) => {
        const followers = p.followers_count ?? p.fan_count;
        return (
          <div className="flex items-center gap-2 min-w-0">
            {p.picture_url && (
              <img src={p.picture_url} alt=""
                className="h-7 w-7 rounded-sm object-cover border border-zinc-200 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <span className="font-display font-bold text-[12px] truncate">
                  {p.name || p.meta_page_id}
                </span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 truncate flex items-center gap-1.5 flex-wrap">
                {p.category && <span>{p.category}</span>}
                {followers > 0 && (
                  <span>· {Number(followers).toLocaleString()} followers</span>
                )}
                {!p.category && !followers && (
                  <span>{p.meta_page_id}</span>
                )}
              </div>
            </div>
          </div>
        );
      },
      resourceId: (p) => String(p.meta_page_id),
      resourceLabel: (p) => p.name || p.meta_page_id,
    },
    {
      key: "instagram",
      label: "Instagram Business",
      icon: <Instagram className="h-3.5 w-3.5" style={{ color: "#E1306C" }} />,
      items: resources.instagram || [],
      renderItem: (ig) => (
        <div className="flex items-center gap-2 min-w-0">
          {ig.profile_picture_url && (
            <img src={ig.profile_picture_url} alt=""
              className="h-7 w-7 rounded-sm object-cover border border-zinc-200 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              {ig.name && (
                <span className="font-display font-bold text-[12px] truncate">
                  {ig.name}
                </span>
              )}
              {ig.username && (
                <span className="font-mono text-[11px] text-[#E1306C] truncate">
                  @{ig.username}
                </span>
              )}
              {!ig.name && !ig.username && (
                <span className="font-mono text-[12px] truncate">
                  {ig.instagram_business_account_id}
                </span>
              )}
            </div>
            <div className="text-[10px] font-mono text-zinc-500 truncate">
              {ig.linked_page_name
                ? <>via {ig.linked_page_name}</>
                : ig.instagram_business_account_id}
            </div>
          </div>
        </div>
      ),
      resourceId: (ig) => String(ig.instagram_business_account_id),
      resourceLabel: (ig) => ig.username
        ? `@${ig.username}` : ig.name || ig.instagram_business_account_id,
    },
    {
      key: "waba",
      label: "WhatsApp Business",
      icon: <MessageSquare className="h-3.5 w-3.5" style={{ color: "#25D366" }} />,
      items: resources.waba || [],
      renderItem: (w) => (
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <span className="font-mono text-[12px] truncate">{w.name || w.id}</span>
          <span className="text-[10px] font-mono text-zinc-500">{w.id}</span>
        </div>
      ),
      resourceId: (w) => String(w.id),
      resourceLabel: (w) => w.name || w.id,
    },
  ];

  const hasAny = sections.some((s) => (s.items?.length || 0) > 0);
  if (!hasAny) {
    return (
      <div className="border-t border-zinc-100 bg-zinc-50/40 px-3 py-4 text-[12px] text-zinc-600">
        No assets are visible to this connection yet. If you just authorised
        Meta, click <strong>Refresh assets</strong> above. If you&apos;re using
        the master OAuth, assign Ad Accounts and Pages to this client from{" "}
        <Link to="/integrations/meta" className="text-[#1877F2] hover:underline">
          the master Meta page
        </Link>.
      </div>
    );
  }

  return (
    <CollapsibleSections sections={sections}
      businessUnits={businessUnits}
      assignments={assignments}
      onAssign={onAssign}
      testidPrefix="meta-resources" />
  );
}

function CollapsibleSections({ sections, businessUnits, assignments, onAssign,
                               testidPrefix }) {
  // Open the first section that has assets by default.
  const initialOpen = useMemo(() => {
    const init = {};
    const first = sections.find((s) => (s.items?.length || 0) > 0);
    if (first) init[first.key] = true;
    return init;
  }, [sections]);
  const [open, setOpen] = useState(initialOpen);
  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="border-t border-zinc-100 bg-zinc-50/40"
      data-testid={testidPrefix}>
      <div className="divide-y divide-zinc-100">
        {sections.map((sec) => {
          const isOpen = !!open[sec.key];
          const count = sec.items?.length || 0;
          return (
            <div key={sec.key} data-testid={`meta-section-${sec.key}`}>
              <button type="button" onClick={() => toggle(sec.key)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-100/60 text-left"
                data-testid={`meta-section-toggle-${sec.key}`}>
                {isOpen
                  ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                  : <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
                {sec.icon}
                <span className="label-overline text-[11px]">{sec.label}</span>
                <span className="font-mono text-[10px] text-zinc-400">({count})</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-1.5">
                  {count === 0 ? (
                    <div className="text-[11px] italic text-zinc-500">
                      None discovered.
                    </div>
                  ) : (
                    sec.items.map((item, i) => {
                      const rid = sec.resourceId(item) || `idx-${i}`;
                      const rlabel = sec.resourceLabel(item) || "";
                      const aKey = `${sec.key}::${rid}`;
                      const asg = assignments?.[aKey];
                      const selValue = asg?.status === "ignored"
                        ? "__ignored__"
                        : asg?.business_unit_id || "";
                      return (
                        <div key={rid}
                          className="grid grid-cols-[1fr_240px] gap-2 items-center py-1.5 border-b border-zinc-100 last:border-b-0"
                          data-testid={`meta-asset-${sec.key}-${rid}`}>
                          {sec.renderItem(item)}
                          <PropertySelect
                            value={selValue}
                            businessUnits={businessUnits}
                            onChange={(v) => {
                              if (v === "") {
                                onAssign(sec.key, rid, rlabel, null, "unassigned");
                              } else if (v === "__ignored__") {
                                onAssign(sec.key, rid, rlabel, null, "ignored");
                              } else {
                                onAssign(sec.key, rid, rlabel, v, "assigned");
                              }
                            }}
                            testid={`meta-asset-assign-${sec.key}-${rid}`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PropertySelect({ value, businessUnits, onChange, testid }) {
  const isIgnored = value === "__ignored__";
  return (
    <select value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`h-7 text-[11px] font-mono rounded-sm border px-1.5 truncate ${
        isIgnored
          ? "border-zinc-300 bg-zinc-100 text-zinc-500"
          : value
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-zinc-200 bg-white text-zinc-700"
      }`}
      title={isIgnored ? "Ignored — excluded from reports"
        : value ? "Assigned to a property"
        : "Pick a property or ignore"}>
      <option value="">— Unassigned —</option>
      <optgroup label="Assign to property">
        {(businessUnits || []).map((b) => (
          <option key={b.business_unit_id} value={b.business_unit_id}>
            {b.name || b.business_unit_id}
          </option>
        ))}
      </optgroup>
      <option value="__ignored__">Ignore (exclude from reports)</option>
    </select>
  );
}

function HealthPill({ connection }) {
  const status = connection?.status;
  if (connection?.last_error) {
    return (
      <Badge className="rounded-sm bg-red-50 text-red-800 border-red-200 font-mono text-[10px] gap-1"
        data-testid="meta-health-pill-error"
        title={connection.last_error}>
        <ShieldAlert className="h-3 w-3" /> Error
      </Badge>
    );
  }
  if (status === "active") {
    return (
      <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 font-mono text-[10px] gap-1"
        data-testid="meta-health-pill-ok">
        <ShieldCheck className="h-3 w-3" /> Active
      </Badge>
    );
  }
  return (
    <Badge className="rounded-sm bg-zinc-100 text-zinc-600 border-zinc-200 font-mono text-[10px] gap-1"
      data-testid="meta-health-pill-unknown">
      <ShieldQuestion className="h-3 w-3" /> Unknown
    </Badge>
  );
}
